import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, Subscription, combineLatest, filter, firstValueFrom } from 'rxjs';
import { AuthService } from '@core/services/common/auth.service';
import { TenancyService } from '@core/services/common/tenancy.service';
import { segundoFatorSatisfeito } from '@model/two-factor-current-method.model';
import { OnboardingRoute } from '@core/services/api/routes/tenancy/onboarding.route';
import { NotificationService } from '@core/services/notification.service';
import { EstadoOnboarding, RespostaExercicio, ResultadoExercicio } from './onboarding.model';

/** Estado vem do tenant autenticado. Orientações são registradas à parte dos cadastros salvos. */
@Injectable({ providedIn: 'root' })
export class OnboardingService {
  private readonly api = inject(OnboardingRoute);
  private readonly auth = inject(AuthService);
  private readonly tenancy = inject(TenancyService);
  private readonly router = inject(Router);
  private readonly notificacao = inject(NotificationService);
  private readonly estadoSignal = signal<EstadoOnboarding | null>(null);
  /** Abrir uma tarefa indica intenção de concluir, não comprova um cadastro salvo. */
  private readonly tarefasAbertas = signal<ReadonlySet<string>>(new Set());
  readonly estado = this.estadoSignal.asReadonly();
  readonly ocupado = signal(false);
  readonly erro = signal('');
  /**
   * Falha de uma AÇÃO (salvar progresso, conferir, preferência). Vai para um
   * toast no canto superior direito (dono, 30/09/2026: "não precisa desse
   * alerta gigante"); `erro` fica só para a falha ao CARREGAR, que precisa do
   * "Tentar novamente" na tela.
   */
  readonly erroDaAcao = signal('');
  readonly orbitaLiberada = computed(() => this.estado()?.concluido === true && this.estado()?.celebrado === true);
  readonly emImplantacao = computed(() => this.acompanhamentoAtivo() && !this.estado()?.concluido);
  readonly contexto = signal('');
  /** Primeira leitura da sessão (e o redirecionamento que ela pedir) em curso: a splash espera por ela. */
  readonly abrindo = signal(false);
  private usuarioId = '';
  private segundoFatorValido = false;
  private sessao = '';
  private versao = 0;
  private leitura?: Subscription;
  private sincronizacaoPendente = false;

  constructor() {
    combineLatest([this.auth.currentUserObserver, this.auth.twoFactorMethodObserver]).subscribe(([usuario, fator]) => {
      this.usuarioId = usuario?.id ?? '';
      this.segundoFatorValido = segundoFatorSatisfeito(fator);
      if (this.sincronizarSessao() && this.sessao) this.carregar();
    });
    this.router.events.pipe(filter((evento): evento is NavigationEnd => evento instanceof NavigationEnd)).subscribe(evento => {
      const rota = evento.urlAfterRedirects.split(/[?#]/)[0].replace(/\/+$/, '');
      if (rota === '/painel/primeiros-passos' && this.orbitaLiberada()) void this.router.navigateByUrl('/painel/dashboard');
    });
    const atualizar = () => {
      if (this.ocupado()) this.sincronizacaoPendente = true;
      else this.carregar(false);
    };
    const outraAba = (evento: StorageEvent) => {
      if (this.sessao && evento.key === `mg:onboarding:${this.sessao}`) atualizar();
    };
    window.addEventListener('storage', outraAba);
    window.addEventListener('focus', atualizar);
    inject(DestroyRef).onDestroy(() => {
      window.removeEventListener('storage', outraAba);
      window.removeEventListener('focus', atualizar);
      this.leitura?.unsubscribe();
    });
  }

  carregar(redirecionar = true, reativar = false): void {
    this.sincronizarSessao();
    if (!this.sessao || this.ocupado()) return;
    const versao = this.versao;
    this.leitura?.unsubscribe();
    this.ocupado.set(true);
    this.erro.set('');
    if (!this.estado()) this.abrindo.set(true);
    this.leitura = this.api.carregar().subscribe({
      next: estado => {
        if (!this.sessaoValida(versao)) return;
        this.estadoSignal.set(estado);
        this.ocupado.set(false);
        const noRoteiro = (this.router.currentNavigation?.()?.initialUrl.toString() ?? this.router.url ?? '').split(/[?#]/)[0] === '/painel/primeiros-passos';
        if (reativar && estado.elegivel && !this.orbitaLiberada()) {
          void this.preferencia(true).finally(() => this.abrindo.set(false));
        } else if (this.orbitaLiberada() && noRoteiro) {
          void this.router.navigateByUrl('/painel/dashboard').finally(() => this.abrindo.set(false));
        } else if (redirecionar && this.acompanhamentoAtivo() && !this.orbitaLiberada() && !noRoteiro) {
          void this.router.navigateByUrl('/painel/primeiros-passos').finally(() => this.abrindo.set(false));
        } else {
          this.abrindo.set(false);
        }
        this.sincronizarPendente();
      },
      error: () => {
        if (!this.sessaoValida(versao)) return;
        this.abrindo.set(false);
        this.ocupado.set(false);
        this.erro.set('Não foi possível recuperar os primeiros passos. Tente novamente.');
        this.sincronizarPendente();
      },
    });
  }

  /** A pausa é individual e só sai do roteiro após confirmação do servidor. */
  async pular(): Promise<void> {
    if (await this.preferencia(false)) await this.router.navigateByUrl('/painel/dashboard');
  }

  async abrir(): Promise<void> {
    if (!this.estado()) this.carregar(false, true);
    else await this.preferencia(true);
  }

  async preferencia(ativo: boolean): Promise<boolean> {
    if (this.orbitaLiberada()) return false;
    return !!await this.executar(() => this.api.preferencia(ativo), estado => {
      this.estadoSignal.set(estado);
      if (ativo && estado.ativo && !this.orbitaLiberada()) {
        void this.router.navigateByUrl('/painel/primeiros-passos');
      }
    });
  }

  async guia(modo: 'guiado' | 'pausado', capitulo: string): Promise<boolean> {
    return !!await this.executar(() => this.api.guia(modo, capitulo), estado => this.estadoSignal.set(estado));
  }

  registrarTarefaAberta(criterio: string, contexto: string): void {
    this.sincronizarSessao();
    if (!this.sessao || contexto !== this.contexto() || !this.emImplantacao()) return;
    this.tarefasAbertas.update(tarefas => new Set([...tarefas, criterio]));
  }

  aguardaConclusao(criterio: string): boolean {
    return this.tarefasAbertas().has(criterio);
  }

  async concluirOrientacao(capitulo: string): Promise<boolean> {
    if (this.estado()?.orientacoes?.includes(capitulo)) {
      return true;
    }
    const resultado = await this.executar(() => this.api.concluirOrientacao(capitulo), estado => {
      this.estadoSignal.set(estado);
    });
    return !!resultado;
  }

  async responder(id: string, resposta: RespostaExercicio): Promise<ResultadoExercicio | null> {
    return this.executar(() => this.api.exercicio(id, resposta), resultado => this.estadoSignal.set(resultado.estado));
  }

  async verificar(): Promise<void> {
    await this.executar(() => this.api.verificar(), estado => {
      this.estadoSignal.set(estado);
      this.tarefasAbertas.set(new Set());
      if (this.acompanhamentoAtivo() && estado.concluido && !estado.celebrado) {
        void this.router.navigateByUrl('/painel/primeiros-passos');
      }
    }, true);
  }

  async plano(segmento: string, modeloContabil?: 'micro' | 'pequena'): Promise<void> {
    await this.executar(() => this.api.plano(segmento, modeloContabil), estado => this.estadoSignal.set(estado), true);
  }

  async celebrar(): Promise<void> {
    await this.executar(() => this.api.celebrar(), estado => this.estadoSignal.set(estado));
  }

  private acompanhamentoAtivo(): boolean {
    return this.estado()?.elegivel === true && this.estado()?.ativo === true;
  }

  private sincronizarSessao(): boolean {
    const sessao = this.tenancy.isSubdomain && this.usuarioId && this.segundoFatorValido
      ? `${this.tenancy.subdomain}:${this.usuarioId}` : '';
    if (sessao === this.sessao) return false;
    this.sessao = sessao;
    this.contexto.set(sessao);
    ++this.versao;
    this.leitura?.unsubscribe();
    this.estadoSignal.set(null);
    this.tarefasAbertas.set(new Set());
    this.sincronizacaoPendente = false;
    this.ocupado.set(false);
    this.abrindo.set(false);
    this.erro.set('');
    return true;
  }

  private sessaoValida(versao: number): boolean {
    this.sincronizarSessao();
    return !!this.sessao && versao === this.versao;
  }

  private async executar<T>(criarPedido: () => Observable<T>, aplicar?: (resposta: T) => void, exigeConfiguracao = false): Promise<T | null> {
    this.sincronizarSessao();
    if (!this.sessao || this.ocupado() || !this.estado()?.elegivel || (exigeConfiguracao && !this.estado()?.pode_configurar)) return null;
    const versao = this.versao;
    this.ocupado.set(true);
    this.erroDaAcao.set('');
    try {
      const resposta = await firstValueFrom(criarPedido());
      if (!this.sessaoValida(versao)) return null;
      aplicar?.(resposta);
      try { localStorage.setItem(`mg:onboarding:${this.sessao}`, `${Date.now()}:${Math.random()}`); } catch { /* A API continua sendo a fonte do progresso. */ }
      return resposta;
    } catch (erro) {
      if (this.sessaoValida(versao)) {
        const detalhes = (erro as { error?: { errors?: Record<string, string[]> } })?.error?.errors;
        const mensagem = detalhes ? Object.values(detalhes).flat().join(' ') : 'Não foi possível salvar o progresso. Tente novamente.';
        this.erroDaAcao.set(mensagem);
        this.notificacao.error(mensagem);
      }
      return null;
    } finally {
      if (this.sessaoValida(versao)) {
        this.ocupado.set(false);
        this.sincronizarPendente();
      }
    }
  }

  private sincronizarPendente(): void {
    if (!this.sincronizacaoPendente || this.ocupado()) return;
    this.sincronizacaoPendente = false;
    this.carregar(false);
  }
}
